import { useCallback } from "react";
import { useShallow } from "zustand/react/shallow";
import type { FpMessage, FpMessagePart } from "../../../schemas/codegen";
import { useMessageStore } from "../../state/stores/messageStore";

export function useMessageManagement() {
  const {
    setMessages,
    addMessage,
    updateMessage,
    appendMessageContent,
    clearProjectData,
    addMessagePart,
    updateMessagePart,
  } = useMessageStore(
    useShallow((state) => ({
      clearProjectData: state.clearProjectData,
      setMessages: state.setMessages,
      addMessage: state.addMessage,
      updateMessage: state.updateMessage,
      appendMessageContent: state.appendMessageContent,
      addMessagePart: state.addMessagePart,
      updateMessagePart: state.updateMessagePart,
    })),
  );

  const handleMessageUpdate = useCallback(
    (messageId: string, updates: Partial<FpMessage>) => {
      updateMessage(messageId, updates);
    },
    [updateMessage],
  );

  const handleMessageAppend = useCallback(
    (messageId: string, content: string) => {
      appendMessageContent(messageId, content);
    },
    [appendMessageContent],
  );

  const handleMessagePartAdd = useCallback(
    (messageId: string, part: FpMessagePart, index: number) => {
      addMessagePart(messageId, part, index);
    },
    [addMessagePart],
  );

  const handleMessagePartUpdate = useCallback(
    (
      messageId: string,
      partId: string,
      updates: {
        content?: string;
        status?: "streaming" | "complete" | "pending" | "executing" | "error";
        result?: unknown;
      },
    ) => {
      updateMessagePart(messageId, partId, updates);
    },
    [updateMessagePart],
  );

  return {
    setMessages,
    addMessage,
    updateMessage: handleMessageUpdate,
    appendMessageContent: handleMessageAppend,
    clearProjectData,
    addMessagePart: handleMessagePartAdd,
    updateMessagePart: handleMessagePartUpdate,
  };
}
